// =============================================================================
// VandieZ admin-web -- Vehicle approval queue (/vehicles/queue)
// =============================================================================
// Lists vehicles awaiting admin approval (status='pending'). Newest first
// per the backend ordering. Click a row to open the detail screen where
// the admin renders RC / insurance / fitness / permit / PUC docs and the
// vehicle photos, then approves or rejects.
// =============================================================================

import {
  AlertCircle,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  Download,
  Loader2,
} from "lucide-react";
import type React from "react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import { vehicleAdminApi } from "@/api/client";
import { PageHeader } from "@/components/AdminLayout";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { TableSkeleton } from "@/components/ui/skeleton";
import { useAuth } from "@/lib/auth";
import { useToast } from "@/lib/toast";
import { cn } from "@/lib/utils";
import { ApiError } from "@/types/auth";
import type { VehicleAdminQueueEntry } from "@/types/vehicle";

const PAGE_SIZE_OPTIONS = [50, 100, 200] as const;
const DEFAULT_PAGE_SIZE = 50;

function formatCreatedAt(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString();
}

export function VehicleQueuePage(): React.JSX.Element {
  const { accessToken } = useAuth();
  const navigate = useNavigate();
  const toast = useToast();
  const [items, setItems] = useState<VehicleAdminQueueEntry[]>([]);
  const [total, setTotal] = useState<number>(0);
  const [offset, setOffset] = useState<number>(0);
  const [pageSize, setPageSize] = useState<number>(DEFAULT_PAGE_SIZE);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [exporting, setExporting] = useState<boolean>(false);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [confirmingBulk, setConfirmingBulk] = useState<boolean>(false);
  const [bulkSubmitting, setBulkSubmitting] = useState<boolean>(false);

  const fetchPage = useCallback(
    async (nextOffset: number, signal?: AbortSignal) => {
      if (accessToken === null) {
        return;
      }
      setLoading(true);
      setError(null);
      try {
        const res = await vehicleAdminApi.queue(
          accessToken,
          { limit: pageSize, offset: nextOffset },
          signal,
        );
        setItems(res.items);
        setTotal(res.total);
        setOffset(res.offset);
        // Selection is per-page; the operator picked rows visible on
        // the previous page, not whatever lands here next.
        setSelected(new Set());
      } catch (e: unknown) {
        if (e instanceof ApiError) {
          setError(e.problem.detail);
        } else if (e instanceof Error && e.name === "AbortError") {
          // ignore
        } else {
          setError("Unexpected error. Try again.");
        }
      } finally {
        setLoading(false);
      }
    },
    [accessToken, pageSize],
  );

  useEffect(() => {
    const controller = new AbortController();
    void fetchPage(0, controller.signal);
    return () => {
      controller.abort();
    };
  }, [fetchPage]);

  const exportCsv = async (): Promise<void> => {
    if (accessToken === null) {return;}
    setExporting(true);
    try {
      const res = await vehicleAdminApi.queue(accessToken, {
        limit: 200,
        offset: 0,
      });
      const header = [
        "registration_number",
        "make",
        "model",
        "owner_name",
        "vahan_verified",
        "created_at",
      ];
      const escape = (v: string): string =>
        /[",\n]/.test(v) ? `"${v.replace(/"/g, '""')}"` : v;
      const csv = [
        header.join(","),
        ...res.items.map((r) =>
          [
            r.registration_number,
            r.make,
            r.model,
            r.owner_name,
            r.vahan_verified ? "yes" : "no",
            r.created_at,
          ]
            .map((c) => escape(String(c)))
            .join(","),
        ),
      ].join("\n");
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `vandiez-vehicle-queue-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } finally {
      setExporting(false);
    }
  };

  const canPrev = offset > 0;
  const canNext = offset + items.length < total;
  const allSelected = items.length > 0 && selected.size === items.length;
  const someSelected = selected.size > 0 && selected.size < items.length;

  const toggleRow = (id: string): void => {
    setSelected((cur) => {
      const next = new Set(cur);
      if (next.has(id)) { next.delete(id); } else { next.add(id); }
      return next;
    });
  };

  const toggleAll = (): void => {
    if (allSelected) {
      setSelected(new Set());
    } else {
      setSelected(new Set(items.map((r) => r.id)));
    }
  };

  const submitBulkApprove = async (): Promise<void> => {
    if (accessToken === null || selected.size === 0) {return;}
    setBulkSubmitting(true);
    setError(null);
    try {
      const res = await vehicleAdminApi.bulkApprove(accessToken, {
        vehicle_ids: Array.from(selected),
      });
      const parts: string[] = [];
      if (res.approved > 0) {parts.push(`${res.approved} approved`);}
      if (res.skipped > 0) {parts.push(`${res.skipped} skipped`);}
      if (res.not_found > 0) {parts.push(`${res.not_found} not found`);}
      const summary = parts.length > 0 ? parts.join(", ") : "no rows changed";
      if (res.approved > 0) {
        toast.success(`Bulk approve: ${summary}.`);
      } else {
        toast.error(`Bulk approve: ${summary}.`);
      }
      setConfirmingBulk(false);
      setSelected(new Set());
      await fetchPage(0);
    } catch (e: unknown) {
      const msg = e instanceof ApiError ? e.problem.detail : "Unexpected error.";
      setError(msg);
      toast.error(`Bulk approve failed: ${msg}`);
    } finally {
      setBulkSubmitting(false);
    }
  };

  const selectedLabels = useMemo(
    () =>
      items
        .filter((r) => selected.has(r.id))
        .slice(0, 3)
        .map((r) => r.registration_number),
    [items, selected],
  );

  return (
    <div className="space-y-4">
      <PageHeader
        title="Vehicle approval queue"
        description="Vehicles awaiting approval. Newest submission first. Click a row to view documents and photos."
        actions={
          <Button
            variant="outline"
            onClick={() => void exportCsv()}
            disabled={exporting || items.length === 0}
          >
            <Download className="mr-1 h-4 w-4" />
            {exporting ? "Exporting…" : "Export CSV"}
          </Button>
        }
      />
        <Card>
          <CardHeader>
            <CardTitle>Pending vehicles</CardTitle>
            <CardDescription>
              Newest submission first. Click a row to view documents and photos.
              {total > 0 && (
                <span className="ml-1 text-foreground">
                  Showing {offset + 1}–{offset + items.length} of {total}.
                </span>
              )}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {error !== null && (
              <Alert variant="destructive" className="mb-4">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            {selected.size > 0 && (
              <div className="mb-3 flex items-center justify-between rounded-md border bg-muted/40 px-3 py-2 text-sm">
                <div className="flex items-center gap-2">
                  <span className="font-medium">{selected.size} selected</span>
                  {selectedLabels.length > 0 && (
                    <span className="text-xs text-muted-foreground font-mono">
                      ({selectedLabels.join(", ")}
                      {selected.size > selectedLabels.length ? ", …" : ""})
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => { setSelected(new Set()); }}
                    disabled={bulkSubmitting}
                  >
                    Clear
                  </Button>
                  {confirmingBulk ? (
                    <>
                      <span className="text-xs text-muted-foreground">
                        Approve {selected.size}?
                      </span>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => { setConfirmingBulk(false); }}
                        disabled={bulkSubmitting}
                      >
                        Cancel
                      </Button>
                      <Button
                        size="sm"
                        onClick={() => void submitBulkApprove()}
                        disabled={bulkSubmitting}
                      >
                        {bulkSubmitting ? (
                          <>
                            <Loader2 className="mr-1 h-3 w-3 animate-spin" />
                            Approving…
                          </>
                        ) : (
                          <>
                            <CheckCircle2 className="mr-1 h-3 w-3" />
                            Confirm
                          </>
                        )}
                      </Button>
                    </>
                  ) : (
                    <Button
                      size="sm"
                      onClick={() => { setConfirmingBulk(true); }}
                    >
                      <CheckCircle2 className="mr-1 h-3 w-3" />
                      Approve {selected.size}
                    </Button>
                  )}
                </div>
              </div>
            )}

            {loading && items.length === 0 ? (
              <TableSkeleton columns={7} />
            ) : items.length === 0 ? (
              <div className="py-10 text-center text-muted-foreground">
                Queue is empty. No vehicles awaiting approval.
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="border-b text-left text-xs uppercase tracking-wide text-muted-foreground">
                    <tr>
                      <th className="py-2 pr-2 w-6">
                        <input
                          type="checkbox"
                          aria-label="Select all on this page"
                          checked={allSelected}
                          ref={(el) => {
                            if (el !== null) {el.indeterminate = someSelected;}
                          }}
                          onChange={toggleAll}
                          className="cursor-pointer"
                        />
                      </th>
                      <th className="py-2 pr-4">Registration</th>
                      <th className="py-2 pr-4">Vehicle</th>
                      <th className="py-2 pr-4">Owner</th>
                      <th className="py-2 pr-4">Vahan</th>
                      <th className="py-2 pr-4">Created</th>
                      <th className="py-2 pr-4"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((row) => {
                      const isSelected = selected.has(row.id);
                      return (
                        <tr
                          key={row.id}
                          className={cn(
                            "border-b last:border-b-0 hover:bg-muted/40",
                            isSelected && "bg-muted/30",
                          )}
                        >
                          <td className="py-3 pr-2">
                            <input
                              type="checkbox"
                              aria-label={`Select ${row.registration_number}`}
                              checked={isSelected}
                              onChange={() => { toggleRow(row.id); }}
                              onClick={(e) => { e.stopPropagation(); }}
                              className="cursor-pointer"
                            />
                          </td>
                          <td
                            className="py-3 pr-4 font-mono text-xs cursor-pointer"
                            onClick={() => { navigate(`/vehicles/${row.id}`); }}
                          >
                            {row.registration_number}
                          </td>
                          <td
                            className="py-3 pr-4 cursor-pointer"
                            onClick={() => { navigate(`/vehicles/${row.id}`); }}
                          >
                            {row.make} {row.model}
                          </td>
                          <td
                            className="py-3 pr-4 cursor-pointer"
                            onClick={() => { navigate(`/vehicles/${row.id}`); }}
                          >
                            {row.owner_name}
                          </td>
                          <td
                            className="py-3 pr-4 cursor-pointer"
                            onClick={() => { navigate(`/vehicles/${row.id}`); }}
                          >
                            {row.vahan_verified ? (
                              <span className="rounded-full bg-success-subtle px-2 py-0.5 text-xs text-success-subtle-foreground">
                                Verified
                              </span>
                            ) : (
                              <span className="rounded-full bg-warning-subtle px-2 py-0.5 text-xs text-warning-subtle-foreground">
                                Not verified
                              </span>
                            )}
                          </td>
                          <td
                            className="py-3 pr-4 text-muted-foreground cursor-pointer"
                            onClick={() => { navigate(`/vehicles/${row.id}`); }}
                          >
                            {formatCreatedAt(row.created_at)}
                          </td>
                          <td className="py-3 pr-4">
                            <Button
                              variant="link"
                              size="sm"
                              className="px-0"
                              onClick={() => { navigate(`/vehicles/${row.id}`); }}
                            >
                              Review →
                            </Button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}

            <div className="mt-4 flex items-center justify-between">
              <Button
                variant="outline"
                size="sm"
                disabled={!canPrev || loading}
                onClick={() => void fetchPage(Math.max(0, offset - pageSize))}
              >
                <ChevronLeft className="mr-1 h-4 w-4" /> Previous
              </Button>
              <div className="flex items-center gap-3 text-xs text-muted-foreground">
                <span>
                  {loading ? "Loading…" : `Page ${Math.floor(offset / pageSize) + 1}`}
                </span>
                <label className="flex items-center gap-1">
                  Rows:
                  <select
                    value={pageSize}
                    onChange={(e) => {
                      setPageSize(Number.parseInt(e.target.value, 10));
                    }}
                    className="rounded border bg-background px-1 py-0.5 text-xs"
                  >
                    {PAGE_SIZE_OPTIONS.map((opt) => (
                      <option key={opt} value={opt}>{opt}</option>
                    ))}
                  </select>
                </label>
              </div>
              <Button
                variant="outline"
                size="sm"
                disabled={!canNext || loading}
                onClick={() => void fetchPage(offset + pageSize)}
              >
                Next <ChevronRight className="ml-1 h-4 w-4" />
              </Button>
            </div>
          </CardContent>
        </Card>
    </div>
  );
}
